import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

export default function AuthConfirm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const verifyToken = async () => {
      const tokenHash = searchParams.get('token_hash');
      const typeParam = searchParams.get('type');
      const redirectTo = searchParams.get('redirect_to') || '/';

      console.log('[AuthConfirm] Starting verification:', { 
        tokenHash: tokenHash?.slice(0, 10) + '...', 
        type: typeParam, 
        redirectTo 
      });

      if (!tokenHash || !typeParam) {
        console.error('[AuthConfirm] Missing parameters:', { tokenHash: !!tokenHash, type: !!typeParam });
        setErrorMessage('Invalid confirmation link. Missing required parameters.');
        setStatus('error');
        return;
      }

      // Map the type parameter to Supabase's expected values
      const isRecovery = typeParam === 'recovery';
      const otpType = isRecovery ? 'recovery' : 
                      typeParam === 'signup' ? 'signup' : 
                      typeParam === 'magiclink' ? 'magiclink' : 
                      typeParam === 'email_change' ? 'email_change' : 'signup';

      console.log('[AuthConfirm] OTP type mapping:', { typeParam, otpType, isRecovery });

      try {
        const { data, error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: otpType,
        });

        console.log('[AuthConfirm] Verification result:', { 
          hasData: !!data, 
          hasSession: !!data?.session,
          hasUser: !!data?.user,
          error 
        });

        if (error) {
          console.error('[AuthConfirm] Token verification error:', error);
          let friendlyMessage = error.message;
          if (error.message.includes('expired') || error.message.includes('invalid')) {
            friendlyMessage = 'This link has expired or has already been used. Please request a new one.';
          }
          setErrorMessage(friendlyMessage);
          setStatus('error');
          return;
        }

        setStatus('success');
        
        // For recovery, redirect IMMEDIATELY to password update page
        // This prevents race conditions with auth state listeners
        if (isRecovery) {
          console.log('[AuthConfirm] Recovery type detected - redirecting to password update NOW');
          navigate('/auth?mode=update-password', { replace: true });
          return;
        }
        
        // For other types, use a brief delay then redirect
        setTimeout(() => {
          console.log('[AuthConfirm] Non-recovery type - redirecting to:', redirectTo);
          try {
            const url = new URL(redirectTo);
            navigate(url.pathname + url.search, { replace: true });
          } catch {
            navigate(redirectTo, { replace: true });
          }
        }, 1500);
      } catch (err: any) {
        console.error('[AuthConfirm] Verification error:', err);
        setErrorMessage(err.message || 'An unexpected error occurred.');
        setStatus('error');
      }
    };

    verifyToken();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md card-elevated">
        <CardHeader className="text-center space-y-4">
          <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center ${
            status === 'loading' ? 'bg-muted' :
            status === 'success' ? 'bg-green-100 dark:bg-green-900/30' :
            'bg-red-100 dark:bg-red-900/30'
          }`}>
            {status === 'loading' && <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />}
            {status === 'success' && <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />}
            {status === 'error' && <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />}
          </div>
          <div className="space-y-2">
            <CardTitle className="text-2xl font-heading">
              {status === 'loading' && 'Verifying...'}
              {status === 'success' && 'Verified!'}
              {status === 'error' && 'Verification Failed'}
            </CardTitle>
            <CardDescription>
              {status === 'loading' && 'Please wait while we verify your link.'}
              {status === 'success' && 'Redirecting you now...'}
              {status === 'error' && errorMessage}
            </CardDescription>
          </div>
        </CardHeader>

        {status === 'error' && (
          <CardContent className="space-y-4">
            <Button 
              onClick={() => navigate('/auth')} 
              className="w-full"
            >
              Back to Sign In
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/auth?mode=reset')} 
              className="w-full"
            >
              Request New Reset Link
            </Button>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
